import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const BusinessQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadQuotes = async () => {
    try {
      let url = '/business/quotes';
      if (filter !== 'all') url += `?statut=${filter}`;
      const res = await api.get(url);
      setQuotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await api.delete(`/business/quotes/${id}`);
      loadQuotes();
    } catch (err) {
      console.error(err);
    }
  };

  const handleConvertToInvoice = async (id) => {
    if (!window.confirm('Convertir ce devis en facture ?')) return;
    try {
      await api.post(`/business/quotes/${id}/convert-to-invoice`);
      loadQuotes();
      alert('Facture créée avec succès !');
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la conversion');
    }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      brouillon: 'bg-gray-100 text-gray-600',
      envoye: 'bg-blue-100 text-blue-600',
      accepte: 'bg-green-100 text-green-600',
      refuse: 'bg-red-100 text-red-600',
      expire: 'bg-amber-100 text-amber-600'
    };
    const labels = {
      brouillon: 'Brouillon',
      envoye: 'Envoyé',
      accepte: 'Accepté',
      refuse: 'Refusé',
      expire: 'Expiré'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[statut] || styles.brouillon}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  // Filtrage par recherche
  const filteredQuotes = quotes.filter(quote => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      quote.numero_devis?.toLowerCase().includes(searchLower) ||
      quote.client_nom?.toLowerCase().includes(searchLower) ||
      quote.client_telephone?.includes(search)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredQuotes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedQuotes = filteredQuotes.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  const stats = {
    total: quotes.length,
    enAttente: quotes.filter(q => ['brouillon', 'envoye'].includes(q.statut)).length,
    acceptes: quotes.filter(q => q.statut === 'accepte').length,
    tauxAcceptation: quotes.length > 0 
      ? Math.round((quotes.filter(q => q.statut === 'accepte').length / quotes.filter(q => ['accepte', 'refuse'].includes(q.statut)).length) * 100) || 0
      : 0,
    valeurEnAttente: quotes
      .filter(q => ['brouillon', 'envoye'].includes(q.statut))
      .reduce((sum, q) => sum + parseFloat(q.total || 0), 0)
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Devis</h1>
          <p className="text-gray-500 text-sm">Gérez vos propositions commerciales</p>
        </div>
        <Link
          to="/workspace/quotes/new"
          className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center justify-center gap-2"
        >
          <span>➕</span> Nouveau devis
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-gray-500 text-xl md:text-2xl mb-1 md:mb-2">📋</div>
          <div className="text-xl md:text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-xs md:text-sm text-gray-500">Total devis</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-blue-500 text-xl md:text-2xl mb-1 md:mb-2">⏳</div>
          <div className="text-xl md:text-2xl font-bold text-blue-600">{stats.enAttente}</div>
          <div className="text-xs md:text-sm text-gray-500">En attente</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-green-500 text-xl md:text-2xl mb-1 md:mb-2">✅</div>
          <div className="text-xl md:text-2xl font-bold text-green-600">{stats.tauxAcceptation}%</div>
          <div className="text-xs md:text-sm text-gray-500">Taux acceptation</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-purple-500 text-xl md:text-2xl mb-1 md:mb-2">💰</div>
          <div className="text-lg md:text-xl font-bold text-purple-600">{stats.valeurEnAttente.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">Valeur en attente</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        {/* Barre de recherche */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher par numéro, client, téléphone..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {[
            { value: 'all', label: 'Tous' },
            { value: 'brouillon', label: '📝 Brouillons' },
            { value: 'envoye', label: '📤 Envoyés' },
            { value: 'accepte', label: '✅ Acceptés' },
            { value: 'refuse', label: '❌ Refusés' },
            { value: 'expire', label: '⏰ Expirés' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                filter === f.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredQuotes.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Quotes List */}
      {paginatedQuotes.length > 0 ? (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">N° Devis</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Client</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Validité</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Montant</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Statut</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedQuotes.map(quote => (
                <tr key={quote.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-indigo-600">{quote.numero_devis}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{quote.client_nom || 'Client anonyme'}</div>
                    {quote.client_telephone && (
                      <div className="text-xs text-gray-400">{quote.client_telephone}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(quote.date_devis).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {quote.date_validite ? (
                      <span className={new Date(quote.date_validite) < new Date() ? 'text-red-500' : 'text-gray-600'}>
                        {new Date(quote.date_validite).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">
                    {parseFloat(quote.total || 0).toLocaleString()} XOF
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(quote.statut)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-2">
                      <Link
                        to={`/workspace/quotes/${quote.id}`}
                        className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                        title="Voir"
                      >
                        👁️
                      </Link>
                      {quote.statut === 'brouillon' && (
                        <Link
                          to={`/workspace/quotes/${quote.id}/edit`}
                          className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition"
                          title="Modifier"
                        >
                          ✏️
                        </Link>
                      )}
                      {quote.statut === 'accepte' && (
                        <button
                          onClick={() => handleConvertToInvoice(quote.id)}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                          title="Convertir en facture"
                        >
                          📄
                        </button>
                      )}
                      {['brouillon', 'envoye'].includes(quote.statut) && (
                        <button
                          onClick={() => handleDelete(quote.id)}
                          className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredQuotes.length)} sur {filteredQuotes.length}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  «
                </button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2">...</span>;
                  }
                  return null;
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">📋</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucun devis'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Essayez une autre recherche' : 'Créez votre premier devis'}
          </p>
          {!search && (
            <Link
              to="/workspace/quotes/new"
              className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
            >
              Créer un devis
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessQuotes;
